import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { CONTACT, PROFILE, REALISATIONS, realisationDetail } from "../config/routes";
import { projects as projectsData } from "../data/projectsData";
import "./styles/Cv.css";

import florianImagePng from "../images/png/background/florian_background.png";
import florianImageAvif from "../images/avif/background/florian_background.avif";
import florianImageWebp from "../images/webp/background/florian_background.webp";

const LINKEDIN_URL = "https://www.linkedin.com/in/florian-da-cunha-a242a21a0/";
const GITHUB_URL = "https://github.com/floriandcdev";
const EMAIL = "floriandc.dev@gmail.com";
const CV_PDF_PATH = "/florian-da-cunha-cv-fullstack.pdf";
const PROFILE_OG_IMAGE =
  "https://dreameragency.fr/images/og/florian-da-cunha-fullstack.jpg";

const featuredProjectSlugs = [
  "autogestia",
  "relyo",
  "hyroos",
  "echo-review",
  "trace",
  "epopee-vf",
];

const projectProofs = {
  autogestia: "Architecture SaaS métier, automatisations et cadrage produit.",
  relyo: "Plateforme avec rôles, données relationnelles et workflows métier.",
  hyroos: "Mobile, camera, scoring et pipeline technique entre app et backend.",
  "echo-review": "SaaS B2B avec dashboard, API, paiement et intégrations Google.",
  trace: "Produit mobile geolocalise avec carte, progression et logique spatiale.",
  "epopee-vf": "Produit web + mobile avec scoring, classements et backend partage.",
};

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="cv-sectionHeading">
      <p className="cv-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

function SkillGroup({ title, items }) {
  return (
    <article className="cv-skillGroup">
      <h3>{title}</h3>
      <div className="cv-tags">
        {items.map((item) => (
          <span className="cv-tag" key={`${title}-${item}`}>
            {item}
          </span>
        ))}
      </div>
    </article>
  );
}

function ExperienceCard({ title, period, intro, items }) {
  return (
    <article className="cv-experienceCard">
      <div>
        <p className="cv-cardMeta">{period}</p>
        <h3>{title}</h3>
        <p>{intro}</p>
      </div>
      <ul className="cv-list">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </article>
  );
}

function ProjectCard({ project }) {
  return (
    <article className="cv-projectCard">
      <div className="cv-projectTop">
        <h3>{project.shortTitle || project.title}</h3>
        <Link to={realisationDetail(project.slug)} className="cv-textLink">
          Voir la fiche
        </Link>
      </div>
      <p>{project.summary || project.subtitle}</p>
      <div className="cv-projectProof">
        <strong>Ce que ça démontre :</strong> {projectProofs[project.slug]}
      </div>
      <div className="cv-tags">
        {(project.stack || []).slice(0, 4).map((item) => (
          <span className="cv-tag" key={`${project.slug}-${item}`}>
            {item}
          </span>
        ))}
      </div>
    </article>
  );
}

export default function CVProfile() {
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 599 : false
  );

  const siteUrl = "https://dreameragency.fr";
  const metaTitle = "Florian Da Cunha - Développeur Full-Stack React / Node.js / TypeScript";
  const metaDesc =
    "Développeur full-stack Bac+5 spécialisé React, Node.js et TypeScript. Expérience SaaS, applications web/mobile, API, bases de données, intégrations et déploiement.";
  const ogDescription =
    "Développeur full-stack Bac+5 spécialisé React, Node.js et TypeScript. SaaS, applications web/mobile et solutions métier.";
  const canonicalUrl = `${siteUrl}${PROFILE}`;

  const heroBadges = [
    "Toulouse",
    "Paris / hybride",
    "React",
    "Node.js",
    "TypeScript",
    "SaaS / applications métier",
  ];

  const stackGroups = [
    {
      title: "Frontend",
      items: ["React", "Vite", "JavaScript", "TypeScript", "HTML/CSS"],
    },
    {
      title: "Backend",
      items: ["Node.js", "Express", "REST API", "Logique métier"],
    },
    {
      title: "Bases de données",
      items: ["MySQL", "PostgreSQL", "Firebase", "Supabase"],
    },
    {
      title: "Mobile",
      items: ["Flutter", "React Native", "Expo"],
    },
    {
      title: "Intégrations",
      items: ["Stripe", "PayPal", "OAuth Google", "Gmail API", "FCM", "OpenAI / API IA"],
    },
    {
      title: "DevOps",
      items: ["Linux", "Nginx", "PM2", "SSL", "Déploiement VPS"],
    },
    {
      title: "SEO / analytics",
      items: ["SEO technique", "Open Graph", "Sitemap", "Robots.txt", "GA4", "Search Console"],
    },
  ];

  const featuredProjects = useMemo(
    () =>
      featuredProjectSlugs
        .map((slug) => projectsData.find((project) => project.slug === slug))
        .filter(Boolean),
    []
  );

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Florian Da Cunha",
        jobTitle: "Développeur Full-Stack React / Node.js / TypeScript",
        url: `${siteUrl}${PROFILE}`,
        email: `mailto:${EMAIL}`,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Toulouse",
          addressCountry: "FR",
        },
        sameAs: [LINKEDIN_URL, GITHUB_URL],
      },
    ],
    [siteUrl]
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();

    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }

    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".cvProfile");
    if (!mainContainer) return;

    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  return (
    <main
      className={`cvProfile ${darkMode ? "dark" : "light"}`}
      onClick={handleMainContainerClick}
    >
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={PROFILE_OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:alt" content={metaTitle} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={PROFILE_OG_IMAGE} />
      </Helmet>

      <section className="cv-hero">
        <div className="cv-container cv-heroGrid">
          <div className="cv-heroContent">
            <p className="cv-eyebrow">Profil développeur</p>
            <h1>Florian Da Cunha</h1>
            <p className="cv-heroTitle">
              Développeur Full-Stack React / Node.js / TypeScript
            </p>
            <p className="cv-heroLead">
              Bac+5 en informatique, environ 5 ans d’expérience projet entre logiciel métier
              ERP et produits web/mobile/SaaS développés en freelance.
            </p>

            <div className="cv-tags cv-heroTags" aria-label="Informations clés">
              {heroBadges.map((badge) => (
                <span className="cv-tag" key={badge}>
                  {badge}
                </span>
              ))}
            </div>

            <div className="cv-actions">
              <Link to={REALISATIONS} className="cv-btn cv-btnPrimary">
                Voir les réalisations
              </Link>
              <Link to={CONTACT} className="cv-btn cv-btnSecondary">
                Me contacter
              </Link>
              <a
                href={CV_PDF_PATH}
                download="florian-da-cunha-cv-fullstack.pdf"
                className="cv-btn cv-btnSecondary"
                type="application/pdf"
                aria-label="Télécharger le CV PDF de Florian Da Cunha"
              >
                Télécharger le CV PDF
              </a>
            </div>
          </div>

          <aside className="cv-profilePanel" aria-label="Coordonnées et liens recruteur">
            <picture className="cv-portrait">
              <source srcSet={florianImageAvif} type="image/avif" />
              <source srcSet={florianImageWebp} type="image/webp" />
              <img src={florianImagePng} alt="Florian Da Cunha" />
            </picture>

            <div className="cv-profileFacts">
              <div>
                <span>Localisation</span>
                <strong>Toulouse</strong>
              </div>
              <div>
                <span>Mobilité</span>
                <strong>Paris ou hybride</strong>
              </div>
              <div>
                <span>Recherche</span>
                <strong>CDI full-stack</strong>
              </div>
            </div>

            <div className="cv-socialLinks">
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                LinkedIn
              </a>
              <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
                GitHub
              </a>
              <a href={`mailto:${EMAIL}`}>Email</a>
            </div>
          </aside>
        </div>
      </section>

      <section className="cv-section">
        <div className="cv-container cv-summaryGrid">
          <SectionHeading eyebrow="Résumé" title="Profil">
            Développeur full-stack capable de concevoir, développer, déployer et maintenir
            des applications web, mobiles et SaaS de bout en bout.
          </SectionHeading>

          <div className="cv-summaryText">
            <p>
              Mon parcours combine trois ans d’alternance en environnement ERP / logiciel
              métier et deux à trois ans d’activité indépendante avec Dreamer Agency.
              Cette double expérience m’a habitué à travailler à la fois sur du code
              existant, des contraintes de production et des produits à construire depuis zéro.
            </p>
            <p>
              J’aime comprendre le besoin métier avant de coder : utilisateurs, données,
              règles, risques, maintenance. C’est ce qui me permet de passer du frontend au
              backend avec une vision produit cohérente, sans perdre le sens du système global.
            </p>
            <p>
              Je cherche aujourd’hui un CDI développeur full-stack, principalement autour de
              React, Node.js et TypeScript, idéalement sur des sujets SaaS, produit,
              applications métier, dashboards ou mobile.
            </p>
          </div>
        </div>
      </section>

      <section className="cv-section cv-sectionAlt">
        <div className="cv-container">
          <SectionHeading eyebrow="Expérience" title="Deux environnements complémentaires">
            Une base rigoureuse en logiciel métier, puis des projets web/mobile/SaaS livrés
            de bout en bout en freelance.
          </SectionHeading>

          <div className="cv-experienceGrid">
            <ExperienceCard
              title="Environnement ERP / logiciel métier"
              period="3 ans en alternance"
              intro="Maintenance et évolution de modules dans un contexte métier structuré, avec code existant et contraintes de production."
              items={[
                "C, PL/I et SQL propriétaire.",
                "Maintenance, corrections et évolutions de modules.",
                "Compréhension de règles métier complexes.",
                "Lecture de code existant et analyse d’impact.",
                "Rigueur sur des systèmes déjà en production.",
              ]}
            />
            <ExperienceCard
              title="Dreamer Agency / freelance"
              period="2 à 3 ans en auto-entrepreneur"
              intro="Conception et livraison de produits digitaux complets pour projets clients, SaaS, applications mobiles et outils métier."
              items={[
                "Applications web, dashboards, SaaS et apps mobiles.",
                "Frontend React/Vite, backend Node.js/Express et bases de données.",
                "Intégrations API, paiements, OAuth, emails et notifications.",
                "Déploiement, Nginx, PM2, SSL, maintenance et support.",
                "Relation client, cadrage fonctionnel et priorisation.",
              ]}
            />
          </div>
        </div>
      </section>

      <section className="cv-section">
        <div className="cv-container">
          <SectionHeading eyebrow="Stack" title="Stack technique pratiquée en projet">
            Une stack orientée produit full-stack, présentée sans sur-promesse : technologies
            utilisées selon les besoins réels des projets.
          </SectionHeading>

          <div className="cv-stackGrid">
            {stackGroups.map((group) => (
              <SkillGroup title={group.title} items={group.items} key={group.title} />
            ))}
          </div>
        </div>
      </section>

      <section className="cv-section cv-sectionAlt">
        <div className="cv-container">
          <SectionHeading eyebrow="Preuves" title="Projets représentatifs">
            Quelques projets qui illustrent la capacité à livrer des produits complets :
            frontend, backend, base de données, API, intégrations et déploiement.
          </SectionHeading>

          <div className="cv-projectGrid">
            {featuredProjects.map((project) => (
              <ProjectCard project={project} key={project.slug} />
            ))}
          </div>
        </div>
      </section>

      <section className="cv-section">
        <div className="cv-container cv-valueGrid">
          <SectionHeading eyebrow="Équipe" title="Ce que j’apporte">
            Une posture full-stack pragmatique, avec une attention particulière au métier,
            à la maintenabilité et à la livraison.
          </SectionHeading>

          <ul className="cv-valueList">
            {[
              "Autonomie sur un périmètre complet, de l’interface au déploiement.",
              "Capacité à comprendre un besoin métier et à le traduire en parcours simple.",
              "Expérience sur code existant, systèmes complexes et contraintes de production.",
              "Passage fluide entre frontend, backend, données et intégrations.",
              "Attention portée au déploiement, à la maintenance et aux évolutions.",
              "Communication claire avec clients, équipes techniques ou interlocuteurs métier.",
              "Envie de rejoindre une équipe produit / SaaS / full-stack.",
            ].map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="cv-section cv-sectionAlt">
        <div className="cv-container cv-availability">
          <div>
            <p className="cv-eyebrow">Recherche</p>
            <h2>Disponibilité / mobilité</h2>
            <p>
              Je recherche un CDI développeur full-stack, avec une cible principale React /
              Node.js / TypeScript. Je suis ouvert à Paris ou à un format hybride ; Toulouse
              reste possible selon l’opportunité.
            </p>
          </div>
          <div className="cv-availabilityCard">
            <span>Format recherché</span>
            <strong>CDI full-stack</strong>
            <span>Domaines d’intérêt</span>
            <strong>SaaS, produit, applications métier, web/mobile</strong>
          </div>
        </div>
      </section>

      <section className="cv-finalCta">
        <div className="cv-container cv-finalCtaInner">
          <div>
            <p className="cv-eyebrow">Contact recruteur</p>
            <h2>Échanger sur un poste full-stack</h2>
            <p>
              Pour un poste React / Node.js / TypeScript, un produit SaaS, une application
              métier ou une équipe qui cherche un profil capable de livrer proprement.
            </p>
          </div>
          <div className="cv-finalActions">
            <Link to={CONTACT} className="cv-btn cv-btnPrimary">
              Me contacter
            </Link>
            <Link to={REALISATIONS} className="cv-btn cv-btnSecondary">
              Voir les réalisations
            </Link>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="cv-textLink">
              LinkedIn
            </a>
            <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer" className="cv-textLink">
              GitHub
            </a>
          </div>
        </div>
      </section>

      {jsonLd.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </main>
  );
}
